/**
 * What the flow designer works out from a definition (docs/en/40, docs/en/47 §3-§4): which
 * templates go through a flow and which actions run on them, where each state is drawn, the
 * arrows its transitions make, what moves a record into a state - a button or an automation -
 * and what is wrong with a flow before the server is asked.
 *
 * <p>Pure: nothing here draws or saves. The designer (components/flows/FlowDesigner.tsx) and
 * its tests use the same functions.
 */
import {
  lifecycleOf, type FieldDefinition, type FlowDefinition, type RegistryDefinition, type StateDefinition,
  type StateRole, type TransitionDefinition, type TypeDefinition
} from './registry';

export const STATE_ROLES: StateRole[] = ['start', 'won', 'lost', 'done'];

/** A role's states end the work: a record there is finished. */
export const ENDING_ROLES: StateRole[] = ['won', 'lost', 'done'];

/** The closed vocabulary of an action's effects (common-lib ActionTypeDef.EffectKind). */
export const EFFECT_KINDS = ['move', 'set', 'create', 'note', 'message', 'link', 'unlink', 'issue', 'issue-document',
  'compute', 'emit', 'repoint', 'unrepoint'] as const;
export type EffectKind = (typeof EFFECT_KINDS)[number];

export interface Precondition { when: string; refusal?: Record<string, string> }

export interface Effect {
  kind: EffectKind;
  field?: string;
  value?: string;
  state?: string;
  template?: string;
  values?: Record<string, string>;
  link?: string;
  to?: string;
  as?: string;
  message?: string;
  binding?: string;
  event?: string;
  on?: string;
  labels?: Record<string, string>;
  ifInstalled?: boolean;
}

export interface ActionDefinition {
  key: string;
  labels?: Record<string, string>;
  target?: string;
  parameters?: FieldDefinition[];
  preconditions?: Precondition[];
  warnings?: Precondition[];
  effects?: Effect[];
  permission?: string;
  when?: { on: string; state?: string };
  style?: string;
  records?: string;
  [other: string]: unknown;
}

// ------------------------------------------------------------------ what goes through a flow

function typesOf(definition: RegistryDefinition | null | undefined): Map<string, TypeDefinition> {
  return new Map((definition?.types ?? []).map((t) => [t.key, t]));
}

/** A template and the ones above it, nearest first. */
export function lineage(definition: RegistryDefinition | null | undefined, key: string): string[] {
  const types = typesOf(definition);
  const out: string[] = [];
  for (let at = types.get(key); at && !out.includes(at.key); at = at.parent ? types.get(at.parent) : undefined) out.push(at.key);
  return out;
}

/** Every template that goes through a flow: naming it, or taking it from one above that does. */
export function templatesOnFlow(definition: RegistryDefinition | null | undefined, flowKey: string): TypeDefinition[] {
  return (definition?.types ?? []).filter((t) => lifecycleOf(definition, t.key)?.flow?.key === flowKey);
}

/** The flow a template goes through, if any. */
export function flowOf(definition: RegistryDefinition | null | undefined, typeKey: string | undefined): FlowDefinition | undefined {
  return typeKey ? lifecycleOf(definition, typeKey)?.flow : undefined;
}

export function actionsOf(definition: RegistryDefinition | null | undefined): ActionDefinition[] {
  const list = (definition as { actions?: unknown } | null | undefined)?.actions;
  return Array.isArray(list) ? list as ActionDefinition[] : [];
}

/**
 * The actions that run on what goes through a flow: an action on a template runs on the ones
 * below it too, so one on the flow's template, on one above it or on one below it is shown.
 */
export function actionsOnFlow(definition: RegistryDefinition | null | undefined, flowKey: string): ActionDefinition[] {
  const on = templatesOnFlow(definition, flowKey).map((t) => t.key);
  const reach = new Set<string>();
  for (const key of on) lineage(definition, key).forEach((k) => reach.add(k));
  return actionsOf(definition).filter((a) => a.target && reach.has(a.target)
    && (on.includes(a.target) || flowOf(definition, a.target)?.key === flowKey || !flowOf(definition, a.target)));
}

/** The boards drawn from a flow: naming it, or showing a template that goes through it. */
export function boardsOnFlow(definition: RegistryDefinition | null | undefined, flowKey: string): { key: string; labels?: Record<string, string> }[] {
  const boards = ((definition as { boards?: unknown } | null | undefined)?.boards ?? []) as { key: string; labels?: Record<string, string>; flow?: string; shows?: string }[];
  return boards.filter((b) => b.flow === flowKey || (!b.flow && flowOf(definition, b.shows)?.key === flowKey));
}

// ------------------------------------------------------------------ names of states

/** Whether a name a transition or an effect uses - a key, `@role` or `*` - names a state. */
export function names(name: string, state: StateDefinition): boolean {
  if (name === '*') return true;
  if (name.startsWith('@')) return (state.roles ?? []).includes(name.slice(1) as StateRole);
  return name === state.key;
}

export function statesNamed(flow: FlowDefinition, name: string): StateDefinition[] {
  return flow.states.filter((s) => names(name, s));
}

export function isEnding(state: StateDefinition): boolean {
  return (state.roles ?? []).some((r) => ENDING_ROLES.includes(r));
}

export function isStart(state: StateDefinition): boolean {
  return (state.roles ?? []).includes('start');
}

// ------------------------------------------------------------------ arrows

export interface Arrow {
  /** A state's key, `*` for "any state", or `@open` for every state that does not end the work. */
  from: string;
  to: string;
  /** Which transition draws it. */
  index: number;
  /** The role it was written to, when the transition names one: `@lost`. */
  viaRole?: string;
}

/** The arrows a flow's transitions make: one from each state they leave from to each they reach. */
export function arrowsOf(flow: FlowDefinition, bundle = false): Arrow[] {
  const out: Arrow[] = [];
  const open = flow.states.filter((s) => !isEnding(s)).map((s) => s.key);
  (flow.transitions ?? []).forEach((t, index) => {
    const targets = statesNamed(flow, t.to);
    const viaRole = t.to.startsWith('@') ? t.to : undefined;
    // "From anything still open" drawn once, from one place, rather than as an arrow from each.
    if (bundle && open.length > 1 && open.every((k) => t.from.includes(k))) {
      for (const to of targets) out.push({ from: '@open', to: to.key, index, viaRole });
      for (const from of t.from.filter((f) => !open.includes(f))) {
        for (const source of statesNamed(flow, from)) for (const to of targets) if (to.key !== source.key) out.push({ from: source.key, to: to.key, index, viaRole });
      }
      return;
    }
    for (const from of t.from) {
      if (from === '*') {
        for (const to of targets) out.push({ from: '*', to: to.key, index, viaRole });
        continue;
      }
      for (const source of statesNamed(flow, from)) {
        for (const to of targets) if (to.key !== source.key) out.push({ from: source.key, to: to.key, index, viaRole });
      }
    }
  });
  return out;
}

/** The states a record in a state may be moved to, and by which transition: every other one for an open flow. */
export function movesFrom(flow: FlowDefinition, from: string): { to: string; index?: number }[] {
  const others = flow.states.filter((s) => s.key !== from);
  const transitions = flow.transitions ?? [];
  if (transitions.length === 0) return others.map((s) => ({ to: s.key }));
  const here = flow.states.find((s) => s.key === from);
  const out: { to: string; index?: number }[] = [];
  for (const state of others) {
    const index = transitions.findIndex((t) => names(t.to, state) && t.from.some((f) => f === '*' || (here != null && names(f, here))));
    if (index >= 0) out.push({ to: state.key, index });
  }
  return out;
}

/**
 * A transition drawn from one state to another. Merged into one already going there from
 * elsewhere when that one asks nothing of its own, so that "anything open may be lost" stays
 * one rule instead of five; a new one otherwise.
 */
export function connect(flow: FlowDefinition, from: string, to: string): FlowDefinition {
  if (from === to) return flow;
  const transitions = flow.transitions ?? [];
  if (movesFrom({ ...flow, transitions }, from).some((m) => m.to === to) && transitions.length > 0) return flow;
  const plain = transitions.findIndex((t) => t.to === to && !t.guard && !(t.requires ?? []).length && !t.permission && !t.from.includes('*'));
  if (plain >= 0) {
    return { ...flow, transitions: transitions.map((t, i) => (i === plain ? { ...t, from: [...t.from, from] } : t)) };
  }
  return { ...flow, transitions: [...transitions, { from: [from], to }] };
}

/** One arrow taken away: the state it leaves from out of its transition, the transition gone when nothing is left. */
export function disconnect(flow: FlowDefinition, arrow: Arrow): FlowDefinition {
  const transitions = (flow.transitions ?? []).flatMap((t, i): TransitionDefinition[] => {
    if (i !== arrow.index) return [t];
    const from = t.from.filter((f) => f !== arrow.from);
    return from.length > 0 ? [{ ...t, from }] : [];
  });
  return { ...flow, transitions: transitions.length > 0 ? transitions : undefined };
}

/**
 * The transitions an open flow would have to stay as free as it is in the order its states are
 * written: each state to the next, back one step, and from anything open to an ending. What a
 * person starts from when they close a flow, rather than drawing every arrow.
 */
export function suggestedTransitions(flow: FlowDefinition): TransitionDefinition[] {
  const open = flow.states.filter((s) => !isEnding(s));
  const out: TransitionDefinition[] = [];
  open.forEach((s, i) => {
    const next = open[i + 1];
    if (next) out.push({ from: [s.key], to: next.key });
    if (i > 0) out.push({ from: [s.key], to: open[i - 1].key });
  });
  for (const ending of flow.states.filter(isEnding)) {
    if (open.length > 0) out.push({ from: open.map((s) => s.key), to: ending.key });
  }
  return out;
}

// ------------------------------------------------------------------ where a state is drawn

export const STATE_W = 172;
export const STATE_H = 64;
const COL_GAP = 76;
const ROW_GAP = 30;
export const ANY_Y = 8;
const TOP = 76;

export interface Spot { x: number; y: number }

/**
 * Where each state is drawn when nobody has moved it: in columns by how many moves it is from
 * the start, the states that end the work in the last column; in the order they are written
 * for a flow that allows every move.
 */
export function autoLayout(flow: FlowDefinition): Record<string, Spot> {
  const rank = new Map<string, number>();
  const states = flow.states;
  const endings = states.filter(isEnding);
  const open = states.filter((s) => !isEnding(s));
  if ((flow.transitions ?? []).length === 0) {
    open.forEach((s, i) => rank.set(s.key, i));
  } else {
    const starts = open.filter(isStart);
    const queue = (starts.length > 0 ? starts : open.slice(0, 1)).map((s) => s.key);
    queue.forEach((k) => rank.set(k, 0));
    while (queue.length > 0) {
      const at = queue.shift()!;
      for (const move of movesFrom(flow, at)) {
        const to = states.find((s) => s.key === move.to);
        if (!to || isEnding(to) || rank.has(to.key)) continue;
        rank.set(to.key, (rank.get(at) ?? 0) + 1);
        queue.push(to.key);
      }
    }
    // Unreachable ones after the rest, in their written order.
    let next = Math.max(-1, ...rank.values()) + 1;
    for (const s of open) if (!rank.has(s.key)) rank.set(s.key, next++);
  }
  const columns = new Map<number, string[]>();
  for (const s of open) {
    const r = rank.get(s.key) ?? 0;
    columns.set(r, [...(columns.get(r) ?? []), s.key]);
  }
  const out: Record<string, Spot> = {};
  const lastOpen = Math.max(-1, ...columns.keys());
  for (const [r, keys] of columns) {
    keys.forEach((k, row) => { out[k] = { x: 24 + r * (STATE_W + COL_GAP), y: TOP + row * (STATE_H + ROW_GAP) }; });
  }
  const endX = 24 + (lastOpen + 1) * (STATE_W + COL_GAP);
  endings.forEach((s, row) => { out[s.key] = { x: endX, y: TOP + row * (STATE_H + ROW_GAP) }; });
  return out;
}

// ------------------------------------------------------------------ buttons and automations

/** The states an action's move effect takes a record to. */
export function movesOf(action: ActionDefinition, flow: FlowDefinition): string[] {
  const out: string[] = [];
  for (const effect of action.effects ?? []) {
    if (effect.kind !== 'move' || !effect.state || effect.on) continue;
    for (const s of statesNamed(flow, effect.state)) if (!out.includes(s.key)) out.push(s.key);
  }
  return out;
}

/** The buttons that bring a record into a state. */
export function actionsInto(actions: ActionDefinition[], flow: FlowDefinition, state: string): ActionDefinition[] {
  return actions.filter((a) => !isAutomation(a) && movesOf(a, flow).includes(state));
}

export function isAutomation(action: ActionDefinition): boolean {
  return action.when?.on === 'entered';
}

/** What runs by itself when a record enters a state - named by its key or by a role it plays. */
export function automationsOn(actions: ActionDefinition[], flow: FlowDefinition, state: string): ActionDefinition[] {
  const here = flow.states.find((s) => s.key === state);
  if (!here) return [];
  return actions.filter((a) => isAutomation(a) && a.when?.state != null && names(a.when.state, here));
}

// ------------------------------------------------------------------ what is wrong

export type FlowProblemCode = 'no-start' | 'many-starts' | 'dead-end' | 'unreachable' | 'missing-target' | 'missing-source'
  | 'action-missing-state' | 'no-states' | 'duplicate-key' | 'bad-key';

export interface FlowProblem { code: FlowProblemCode; state?: string; index?: number; action?: string; name?: string; severity: 'error' | 'warning' }

/**
 * What the designer says is wrong with a flow before anything is saved: no state to begin in,
 * a state no move leaves (and that ends nothing), one nothing reaches, a transition or a button
 * naming a state that is not there. The server checks again; this is so that a person sees it
 * where they are drawing.
 */
export function flowProblems(flow: FlowDefinition, actions: ActionDefinition[] = [], keyShape?: RegExp): FlowProblem[] {
  const out: FlowProblem[] = [];
  if (flow.states.length === 0) return [{ code: 'no-states', severity: 'error' }];
  const seen = new Set<string>();
  for (const s of flow.states) {
    if (seen.has(s.key)) out.push({ code: 'duplicate-key', state: s.key, severity: 'error' });
    seen.add(s.key);
    if (keyShape && !keyShape.test(s.key)) out.push({ code: 'bad-key', state: s.key, severity: 'error' });
  }
  const starts = flow.states.filter(isStart);
  if (starts.length === 0) out.push({ code: 'no-start', severity: 'error' });
  if (starts.length > 1) out.push({ code: 'many-starts', severity: 'warning' });
  const transitions = flow.transitions ?? [];
  transitions.forEach((t, index) => {
    if (statesNamed(flow, t.to).length === 0) out.push({ code: 'missing-target', index, name: t.to, severity: 'error' });
    for (const f of t.from) {
      if (f !== '*' && statesNamed(flow, f).length === 0) out.push({ code: 'missing-source', index, name: f, severity: 'error' });
    }
  });
  if (transitions.length > 0) {
    const reached = new Set<string>(starts.map((s) => s.key));
    const queue = [...reached];
    while (queue.length > 0) {
      const at = queue.shift()!;
      for (const m of movesFrom(flow, at)) if (!reached.has(m.to)) { reached.add(m.to); queue.push(m.to); }
    }
    // A button that moves a record there reaches it too: an action's move goes through the transitions,
    // but a person reading the drawing sees the state as reached by it.
    for (const s of flow.states) {
      if (!isEnding(s) && movesFrom(flow, s.key).length === 0) out.push({ code: 'dead-end', state: s.key, severity: 'warning' });
      if (starts.length > 0 && !reached.has(s.key)) out.push({ code: 'unreachable', state: s.key, severity: 'warning' });
    }
  }
  for (const a of actions) {
    for (const e of a.effects ?? []) {
      if (e.kind === 'move' && e.state && !e.on && statesNamed(flow, e.state).length === 0) {
        out.push({ code: 'action-missing-state', action: a.key, name: e.state, severity: 'error' });
      }
    }
    if (isAutomation(a) && a.when?.state && statesNamed(flow, a.when.state).length === 0) {
      out.push({ code: 'action-missing-state', action: a.key, name: a.when.state, severity: 'error' });
    }
  }
  return out;
}

// ------------------------------------------------------------------ renaming and removing a state

/** A state's key changed everywhere the flow and its actions name it. */
export function renameState(flow: FlowDefinition, actions: ActionDefinition[], from: string, to: string):
  { flow: FlowDefinition; actions: ActionDefinition[] } {
  const swap = (name: string) => (name === from ? to : name);
  const next: FlowDefinition = {
    ...flow,
    states: flow.states.map((s) => (s.key === from ? { ...s, key: to } : s)),
    transitions: flow.transitions?.map((t) => ({ ...t, from: t.from.map(swap), to: swap(t.to) }))
  };
  const nextActions = actions.map((a) => ({
    ...a,
    effects: a.effects?.map((e) => (e.kind === 'move' && e.state === from ? { ...e, state: to } : e)),
    when: a.when?.state === from ? { ...a.when, state: to } : a.when
  }));
  return { flow: next, actions: nextActions };
}

/** A state taken out, with every transition naming it by its key put right. */
export function removeState(flow: FlowDefinition, key: string): FlowDefinition {
  const transitions = (flow.transitions ?? [])
    .filter((t) => t.to !== key)
    .map((t) => ({ ...t, from: t.from.filter((f) => f !== key) }))
    .filter((t) => t.from.length > 0);
  return { ...flow, states: flow.states.filter((s) => s.key !== key), transitions: transitions.length > 0 ? transitions : undefined };
}

// ------------------------------------------------------------------ keys and conditions

/** A key from what a person called something: `Klaar voor levering` → `klaar-voor-levering`. */
export function slug(text: string): string {
  return text.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/ı/g, 'i').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
}

/** A key not yet taken, from a stem: `stem`, `stem-2`, ... */
export function unique(stem: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  let key = stem;
  for (let n = 2; used.has(key); n++) key = `${stem}-${n}`;
  return key;
}

export const OPERATORS = ['==', '!=', '>', '>=', '<', '<=', 'empty', 'filled', 'role', 'not-role'] as const;
export type Operator = (typeof OPERATORS)[number];

/** A condition as the simple builder writes it: a field, how it is compared, with what. */
export interface Condition { field: string; op: Operator; value: string }

/** The expression a simple condition is written as, in the taxonomy's expression language. */
export function writeCondition(c: Condition): string {
  switch (c.op) {
    case 'empty': return `${c.field} == null`;
    case 'filled': return `${c.field} != null`;
    case 'role': return `contains(roles, '${c.value}')`;
    case 'not-role': return `not contains(roles, '${c.value}')`;
    default: {
      const v = c.value.trim();
      // A number, true, false, null or a parameter as it is; anything else is words, quoted.
      const literal = /^(-?\d+(\.\d+)?|true|false|null|\$[A-Za-z][\w.]*)$/.test(v) ? v : `'${v.replace(/'/g, "\\'")}'`;
      return `${c.field} ${c.op} ${literal}`;
    }
  }
}

/** A written expression read back into the simple builder, when it is one; null when it is more than that. */
export function readCondition(expression: string): Condition | null {
  const e = expression.trim();
  let m = /^(not )?contains\(roles, '([a-z]+)'\)$/.exec(e);
  if (m) return { field: '', op: m[1] ? 'not-role' : 'role', value: m[2] };
  m = /^([$A-Za-z][\w.]*) (==|!=) null$/.exec(e);
  if (m) return { field: m[1], op: m[2] === '==' ? 'empty' : 'filled', value: '' };
  m = /^([$A-Za-z][\w.]*) (==|!=|>=|<=|>|<) ('(?:[^'\\]|\\.)*'|-?\d+(?:\.\d+)?|true|false|\$[A-Za-z][\w.]*)$/.exec(e);
  if (m) {
    const raw = m[3].trim();
    const value = /^'.*'$/.test(raw) ? raw.slice(1, -1).replace(/\\'/g, "'") : raw;
    return { field: m[1], op: m[2] as Operator, value };
  }
  return null;
}
